import { useEffect, useMemo } from 'react'

import { Filter } from '@cubejs-client/core'
import { MEETINGS_DIMENSIONS } from 'cube/constants'
import { MeetingCountQuery } from 'cube/queries'
import moment from 'moment'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { ICompoundQuery, IQueryConfig } from 'api/report/query/types'
import useTimePeriod from 'hooks/useTimePeriod'

export interface IMeetingCount {
  metric?: number | null
  previousMetric?: number | null
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
}

const formatSeriesData = (resultSet) =>
  resultSet
    ?.series()
    .flatMap((series) =>
      series.series.map((data) => [formatDate(data.x), data.value])
    )

const formatDate = (date: string) => date.split('T')[0]

// Function that takes a series of data [date, value] and returns a cumulative series of data in [date, cumulativeValue]
const cumulativeSeries = (series) => {
  let cumulative = 0
  return series?.map((data) => {
    cumulative += data[1]
    return [data[0], cumulative]
  })
}

const filterMemberMap = {
  agentId: 'DriftChatAppUsers.userId',
  teamId: 'DriftChatTeamUsers.teamId',
}

const filterOperatorMap = {
  IN: 'equals',
  NOT_IN: 'notEquals',
}

// Since legacy filter component allows for invalid filters
const filterForValidFilters = (query: ICompoundQuery) =>
  query?.property &&
  query?.operation &&
  query?.values &&
  query?.values.length > 0

const mapToPageFilter = (query: ICompoundQuery) =>
  ({
    member: query?.property && filterMemberMap[query.property],
    operator: query?.operation && filterOperatorMap[query.operation],
    values: query?.values?.map((val: number) => String(val)),
  } as Filter)

export const formatPageFilters = (compoundQuery: ICompoundQuery[]) => {
  const filterQuery =
    compoundQuery?.filter(filterForValidFilters)?.map(mapToPageFilter) || []
  return [...filterQuery]
}

export const useMeetingCount = (queryConfig: IQueryConfig): IMeetingCount => {
  const timePeriod = useTimePeriod()

  const pageFilters = formatPageFilters(queryConfig.compoundQuery)

  const cubeQueryCurrent = new CubeQueryBuilder({
    query: MeetingCountQuery,
  })
    .withName('MeetingCountCurrent')
    .withFilters(pageFilters)
    .withTimeDimensions([
      {
        dimension: MEETINGS_DIMENSIONS.SCHEDULED_AT,
        granularity: 'day',
        dateRange: [
          moment(timePeriod.startDate).format('YYYY-MM-DD'),
          moment(timePeriod.endDate).format('YYYY-MM-DD'),
        ],
      },
    ])
    .build()

  const cubeQueryPrevious = new CubeQueryBuilder({
    query: MeetingCountQuery,
  })
    .withName('MeetingCountPrevious')
    .withTimeDimensions([
      {
        dimension: MEETINGS_DIMENSIONS.SCHEDULED_AT,
        granularity: 'day',
        dateRange: [
          moment(timePeriod.prevStart).format('YYYY-MM-DD'),
          moment(timePeriod.prevEnd).format('YYYY-MM-DD'),
        ],
      },
    ])
    .withFilters(pageFilters)
    .build()

  const {
    data: resultSetCurrent,
    refetch: refetchCurrent,
    isLoading: isLoadingCurrent,
    isError: isErrorCurrent,
  } = useCubeQuery({
    query: cubeQueryCurrent,
  })

  const {
    data: resultSetPrevious,
    refetch: refetchPrevious,
    isLoading: isLoadingPrevious,
  } = useCubeQuery({
    query: cubeQueryPrevious,
  })

  const meetingCountCurrent = useMemo(
    () => cumulativeSeries(formatSeriesData(resultSetCurrent)),
    [resultSetCurrent]
  )

  const meetingCountPrevious = useMemo(
    () => cumulativeSeries(formatSeriesData(resultSetPrevious)),
    [resultSetPrevious]
  )

  const metric = useMemo(
    () => meetingCountCurrent ?? null,
    [meetingCountCurrent]
  )

  const previousMetric = useMemo(
    () => meetingCountPrevious ?? null,
    [meetingCountPrevious]
  )

  const isLoading = useMemo(
    () => isLoadingCurrent || isLoadingPrevious,
    [isLoadingCurrent, isLoadingPrevious]
  )

  const hasNoData = useMemo(
    () => !isLoading && !meetingCountCurrent,
    [isLoading, meetingCountCurrent]
  )

  const hasError = useMemo(
    () => !isLoading && isErrorCurrent,
    [isLoading, isErrorCurrent]
  )

  useEffect(() => {
    refetchCurrent()
    refetchPrevious()
  }, [queryConfig.compoundQuery, refetchCurrent, refetchPrevious])

  return useMemo(
    () => ({ metric, previousMetric, isLoading, hasNoData, hasError }),
    [metric, previousMetric, isLoading, hasNoData, hasError]
  )
}

export default useMeetingCount
